import { Pets } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useFetch } from "../../../../../../hooks/Fetching";
import { trimText } from "../../../../../../utils/Functions";
import HorizontalStack from "../../../../components/wrappers/HorizontalStack";
import { useTranslation } from "react-i18next";
import { GET_ARCHIVED_MEDICAL_DAYS_API, MARK_THERAPY_AS_DONE } from "../../../../../../enums/ApiPaths";
import { GET_ARCHIVED_MEDICAL_DAYS, MARK_THERAPY_DONE } from "../../../../../../enums/QueryKeys";
import useCheckPath from "../../../../../../hooks/useCheckPath";

export const medical_pet_id_param = "pet_medical_id";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const cells = ["Date", "Therapy", "MorningDose", "EveningDose"];

export default function MedicalRecords({ pets = [] }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const petId = searchParams.get(medical_pet_id_param);
  const [petName, setPetName] = React.useState("");
 const isArchived = useCheckPath();
 const { id: inpatientId } = useParams();

  React.useEffect(() => {
    if (pets?.length === 0) {
      if (petId || petName) {
        setPetName("");
        setSearchParams({});
      }
      return;
    }

    const petStillInBox = pets.some((p) => String(p.id) === String(petId));

    if (petId && !petStillInBox) {
      setPetName("");
      setSearchParams({});
      return;
    }

    if (!petId && pets?.length > 0) {
      const firstPet = pets[0];
      setPetName(firstPet.name);
      setSearchParams({ [medical_pet_id_param]: firstPet.id });
    }
  }, [pets, petId, petName, setSearchParams]);

  const url = isArchived ? GET_ARCHIVED_MEDICAL_DAYS_API + inpatientId : petId ? `${MARK_THERAPY_AS_DONE}${petId}` : null;
  const key = isArchived ? GET_ARCHIVED_MEDICAL_DAYS + inpatientId : MARK_THERAPY_DONE + petId

  const {
    data = [],
    isLoading,
    error,
  } = useFetch(url, null, key);

  function handleChange(name, id) {
    setPetName(name);
    searchParams.set("pet_medical_id", id);
    setSearchParams(searchParams);
  }

  if (!data || !petId || pets?.length == 0) {
    return (
      <Wrapper>
        <Card>
          <CardContent>
            <Typography>{t("PleaseSelectPet")}</Typography>
          </CardContent>
        </Card>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <RenderPetSelector
        pets={pets}
        petName={petName}
        handleChange={handleChange}
      />
      <RenderMedicalRecords data={data} isLoading={isLoading} error={error} />
    </Wrapper>
  );
}

function Wrapper({ children }) {
  const { t } = useTranslation();
  return (
    <Stack spacing={2} mt={5}>
      <Typography variant="h5" gutterBottom>
        {t("MedicalRecords")}
      </Typography>
      <Typography variant="body1" color={"GrayText"}>
        {t("MedicalRecordsExplanationSubtitle")}
      </Typography>
      {children}
    </Stack>
  );
}

function RenderPetSelector({ pets = [], petName, handleChange }) {
  const { t } = useTranslation();
  const theme = useTheme();
  if (pets?.length <= 1) return null;

  return (
    <Card elevation={2}>
      <Typography margin={2} variant="h5" gutterBottom>
        {t("SelectPet")}
      </Typography>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <Pets />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <FormControl sx={{ width: { xs: "100%", sm: "300px" } }}>
              <InputLabel id="pets-medical-data">{t("ChoosePet")}</InputLabel>
              <Select
                labelId="pets-medical-data"
                value={petName}
                input={<OutlinedInput label={t("ChoosePet")} />}
                MenuProps={MenuProps}
              >
                {pets?.map(({ name, id }) => (
                  <MenuItem
                    key={id}
                    value={name}
                    onClick={() => handleChange(name, id)}
                  >
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function RenderMedicalRecords({ data, isLoading, error }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent>
        <TableContainer sx={{ mt: 2 }} component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {cells.map((cell) => (
                  <TableCell key={cell}>{t(cell)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBodyData data={data} isLoading={isLoading} error={error} />
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

function TableBodyData({ data = [], isLoading, error }) {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={cells.length} align="center" sx={{ py: 6 }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress size={32} />
              <Typography color="text.secondary">
                {t("LoadingRecords")}
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  } else if (error) {
    <TableBody>
      <TableRow>
        <TableCell align="center" colSpan={cells.length} sx={{ py: 8 }}>
          <Stack alignItems="center" spacing={1}>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ opacity: 0.7 }}
            >
              {t("ErrorLoadingPetMedicalRecords")}
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
    </TableBody>;
  } else if (!data || data?.length === 0) {
    return (
      <TableBody>
        <TableRow>
          <TableCell align="center" colSpan={cells.length} sx={{ py: 8 }}>
            <Stack alignItems="center" spacing={1}>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ opacity: 0.7 }}
              >
                {t("NoRecordsFound")}
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody>
      {data?.map((item) => (
        <SingleRow item={item} key={item?.id} />
      ))}
    </TableBody>
  );
}

function SingleRow({ item }) {
  return (
    <TableRow
      key={item.id}
      sx={{
        "&:last-child td": {
          borderBottom: 0,
        },
        height: 70,
      }}
    >
      <TableCell
        sx={{
          pl: 3,
          fontWeight: 500,
          color: "text.primary",
        }}
      >
        {item?.date}
      </TableCell>
      <TableCell sx={{ color: "text.secondary" }} title={item.therapy}>
        {trimText(item.therapy, 30)}
      </TableCell>
      <TableCell
        title={item?.morningDose}
        sx={{
          fontWeight: 600,
          color: "primary.main",
        }}
      >
        {trimText(item?.morningDose, 30) || "-"}
      </TableCell>
      <TableCell
        title={item?.eveningDose}
        sx={{
          fontWeight: 600,
          color: "primary.main",
        }}
      >
        {trimText(item?.eveningDose, 30) || "-"}
      </TableCell>
    </TableRow>
  );
}
